import type { components } from "@patent-studio/api-types";

export type ErrorCode = components["schemas"]["ErrorCode"];

/**
 * What each API error code means to the attorney, in the web's own words, so the server's
 * message (written for the log, and often naming ids) never reaches the screen.
 *
 * Typed by the API's own list, so a new code does not build until it has an entry. `null` is a
 * deliberate nothing: a fault on our side, or a call no screen makes, where the screen's own
 * sentence says it better than the code could; or a refusal the API always writes for the
 * attorney, whose own words are shown instead.
 */
export const ERROR_WORDS: Record<ErrorCode, string | null> = {
  ACCESS_NOT_GRANTED: "This account does not have access to Praxis.",
  ADMISSION_NOT_FOUND:
    "That entry is no longer in the analysis. Reload the page to see the current one.",
  ANALYTICS_IDENTITY_UNAVAILABLE: null,
  BODY_INVALID: null,
  BODY_TOO_LARGE: "That is too large to send. Make it shorter, then try again.",
  CLAIM_NOT_FOUND:
    "That claim is no longer in the set. Reload the page to see the current claims.",
  CLAIM_SET_ITEM_NOT_FOUND:
    "That is no longer in the claim set. Reload the page to see the current set.",
  CLAIM_TOO_LARGE: null,
  CLIENT_NOT_FOUND: "This client could not be found. It may have been deleted.",
  CREDENTIALS_INVALID: null,
  CSRF_REQUIRED:
    "Praxis could not confirm this came from this page. Reload the page, then try again.",
  DRAFT_ALLOWANCE_USED: null,
  DRAFT_BUSY: null,
  DRAFT_DETAILS_LOCKED: null,
  DRAFT_NOT_DELETED: null,
  DRAFT_RUN_ACTIVE:
    "This draft is already being generated. Wait for it to finish.",
  DRAFT_RUN_NOT_FOUND: "This draft has not been generated yet.",
  DRAWING_RENDER_FAILED: "Praxis couldn't produce this figure's drawing.",
  ELEMENT_NOT_FOUND:
    "That element is no longer in the invention. Reload the page to see the current elements.",
  ELEMENT_TOO_LARGE: null,
  EMAIL_NOT_VERIFIED: "Verify your email address, then reload this page.",
  FALLBACK_NOT_FOUND:
    "That fallback is no longer on the element. Reload the page to see the current ones.",
  FILE_NOT_FOUND:
    "That file is no longer on this draft. Reload the page to see the current files.",
  FILE_TOO_LARGE: "That file is too large to upload.",
  FILE_VALIDATION_ERROR: null,
  FRAMEWORK_TOO_LARGE:
    "That change would make the framework larger than Praxis can hold.",
  FRAMEWORK_VERSION_CONFLICT:
    "The framework changed since you opened it. Reload the page, then make the change again.",
  GENERATE_NOT_READY: null,
  INTERNAL_ERROR: null,
  INVENTOR_NOT_ON_DRAFT:
    "That inventor is no longer on this draft. Reload the page to see the current inventors.",
  NOTIFICATION_ACTION_NOT_FOUND:
    "That suggested change is no longer waiting. Reload the page to see the current ones.",
  NOT_SIGNED_IN: "Your session ended. Sign in again; what you saved is kept.",
  NOVELTY_RUN_REFUSED: null,
  OPEN_ITEMS_CHANGED: null,
  POLICY_UNKNOWN: null,
  POLICY_VERSION_MISMATCH:
    "The terms changed since this page was opened. Reload the page to read the current terms.",
  POSITION_AMENDMENT_HISTORY_FULL: null,
  POSITION_NOT_FOUND:
    "That position is no longer in the analysis. Reload the page to see the current positions.",
  QUESTION_NOT_FOUND:
    "That question is no longer open. Reload the page to see the current questions.",
  REGISTRATION_REFUSED: null,
  RUN_INPUTS_NOT_FROZEN: null,
  RUN_NOT_READY: "The draft is not ready to download yet.",
  SERVICE_UNAVAILABLE:
    "Praxis couldn't start that just now. Try again in a moment.",
  SESSION_RENEWAL_UNAVAILABLE: null,
  SIGNATURE_INVALID: null,
  SIGN_IN_FAILED: "Signing in did not finish. Try again.",
  STAGE_LOCKED: null,
  TASK_NOT_FOUND: "This draft could not be found. It may have been deleted.",
  TASK_STATE_CONFLICT:
    "That change can't be made to the draft as it is now. If the page looks out of date, reload it.",
  TASK_STORE_ERROR: null,
  TRY_AGAIN: "Praxis is busy just now. Try again in a moment.",
  UNKNOWN_CPC_CODE:
    "That classification code is not in the CPC scheme. Check it, then try again.",
};

/** The web's sentence for a code, or null for a code with none or one it does not know. */
export function errorWords(code: string | null): string | null {
  return code !== null && Object.hasOwn(ERROR_WORDS, code)
    ? ERROR_WORDS[code as ErrorCode]
    : null;
}
